import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { cn } from "@/lib/utils";

interface AssignRescueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderUniqueId?: string;
  tractorName?: string | null;
  currentRescueOperatorId?: string | null;
}

interface Driver {
  id: string;
  name: string;
  email: string | null;
  order_ids: string[] | null;
}

export function AssignRescueDialog({
  open,
  onOpenChange,
  orderId,
  orderUniqueId,
  tractorName,
  currentRescueOperatorId,
}: AssignRescueDialogProps) {
  const [selectedId, setSelectedId] = useState<string>(
    currentRescueOperatorId ?? ""
  );
  const [comboOpen, setComboOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: allDrivers = [], isLoading } = useQuery({
    queryKey: ["drivers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("id, name, email, order_ids")
        .order("name");
      if (error) throw error;
      return data as Driver[];
    },
    enabled: open,
  });

  // Available = no current order assignments (empty or null order_ids)
  const availableDrivers = useMemo(
    () =>
      allDrivers.filter(
        (d) => !d.order_ids || d.order_ids.length === 0
      ),
    [allDrivers]
  );

  const selectedDriver = allDrivers.find((d) => d.id === selectedId);

  const assignMutation = useMutation({
    mutationFn: async (driverId: string | null) => {
      const { error } = await supabase
        .from("orders")
        .update({ rescue_operator_id: driverId } as any)
        .eq("id", orderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-detail"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Rescue driver assigned");
      onOpenChange(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const handleSave = () => {
    assignMutation.mutate(selectedId || null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Rescue to {orderUniqueId || "Order"}</DialogTitle>
        </DialogHeader>

        <div className="py-2 space-y-3">
          <p className="text-sm text-muted-foreground">
            Select a driver to rescue{" "}
            <span className="font-medium text-foreground">{tractorName || "this tractor"}</span> on{" "}
            <span className="font-medium text-foreground">{orderUniqueId || "this order"}</span>.
          </p>
            
          {isLoading ? (
            <div className="flex flex-col items-center py-8 text-center">
              <Users className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No available drivers found.
              </p>
            </div>
          ) : (
            <Popover open={comboOpen} onOpenChange={setComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboOpen}
                  className="w-full justify-between font-normal"
                >
                  {selectedDriver
                    ? selectedDriver.name
                    : "Select a driver…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search drivers…" />
                  <CommandList>
                    <CommandEmpty>No driver found.</CommandEmpty>
                    <CommandGroup>
                      {availableDrivers.map((driver) => (
                        <CommandItem
                          key={driver.id}
                          value={driver.name}
                          onSelect={() => {
                            setSelectedId(driver.id);
                            setComboOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedId === driver.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          <span>{driver.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <DialogFooter>
          {currentRescueOperatorId && (
            <Button
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={() => assignMutation.mutate(null)}
              disabled={assignMutation.isPending}
            >
              Remove
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={assignMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={assignMutation.isPending || !selectedId}
          >
            {assignMutation.isPending ? "Saving…" : "Assign rescue driver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
